<template>
  <ConfigPageShell
    title="Inventory Flow"
    subtitle="How inventory reaches this account"
    back-to="/"
    back-label="Back to Dashboard"
    dense
  >
    <template #actions>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-refresh"
        :loading="loading"
        data-testid="inventory-flow-refresh"
        @click="loadAll"
      >
        Refresh
      </v-btn>
    </template>

    <v-alert v-if="errorMsg" type="error" variant="tonal" class="mb-4" closable @click:close="errorMsg = ''">
      {{ errorMsg }}
    </v-alert>

    <!-- Loading -->
    <div v-if="loading" class="d-flex align-center justify-center pa-12">
      <v-progress-circular indeterminate color="primary" size="44" />
      <span class="ml-4 text-body-1">Loading...</span>
    </div>

    <!-- No pre-processor configured -->
    <div v-else-if="mode === 'none'" class="text-center pa-12">
      <v-icon icon="mdi-information-outline" size="56" color="grey-lighten-1" class="mb-3" />
      <div class="text-h6 text-grey">No pre-processor configured</div>
      <div class="text-body-2 text-grey mt-1">
        This account doesn't have a group site or shared inventory setup.
        <br />Configure it in the <router-link to="/group-site">Pre-Processor</router-link> page.
      </div>
    </div>

    <!-- Flow Diagram -->
    <div v-else class="flow-container">
      <!-- ISE → Mode row (vertically centered independently of details column) -->
      <div class="flow-primary">
        <!-- Column 1: ISE root node -->
        <div class="flow-col">
          <v-card class="flow-node flow-node--root" variant="flat" elevation="2">
            <v-icon size="28" color="primary" class="mb-1">mdi-engine</v-icon>
            <div class="text-subtitle-2 font-weight-bold">ISE</div>
            <div class="text-caption text-grey">{{ accountId }}</div>
            <v-chip size="x-small" color="primary" variant="tonal" class="mt-2">
              {{ totalVehicles }} VINs
            </v-chip>
          </v-card>
        </div>

        <!-- Connector 1 -->
        <div class="flow-connector">
          <div class="flow-line" />
          <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
        </div>

        <!-- Column 2: Mode node -->
        <div class="flow-col">
          <v-card class="flow-node flow-node--mode" variant="flat" elevation="2">
            <v-icon size="28" :color="mode === 'groupSite' ? 'blue' : 'teal'" class="mb-1">
              {{ mode === 'groupSite' ? 'mdi-source-merge' : 'mdi-source-fork' }}
            </v-icon>
            <div class="text-subtitle-2 font-weight-bold">
              {{ mode === 'groupSite' ? 'Group Site' : 'Shared Inventory' }}
            </div>
            <div class="text-caption text-grey">
              {{ mode === 'groupSite' ? `${sources.length} source(s)` : feedSource || 'N/A' }}
            </div>
          </v-card>
        </div>

        <!-- Connector 2 -->
        <div class="flow-connector">
          <div class="flow-line" />
          <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
        </div>
      </div>

      <!-- Column 3: Details -->
      <div class="flow-col flow-col--details">

        <!-- GROUP SITE: VIN breakdown + source entries -->
        <template v-if="mode === 'groupSite'">
          <!-- VIN Breakdown card (when sourceJazelId data is available) -->
          <v-card v-if="sourceBreakdownAvailable" class="flow-node flow-node--counts mb-3" variant="flat" elevation="1">
            <v-icon size="22" color="green" class="mb-1">mdi-chart-pie</v-icon>
            <div class="text-subtitle-2 font-weight-bold mb-2">VIN Breakdown</div>

            <div class="d-flex align-center mb-1">
              <v-chip size="x-small" color="primary" variant="tonal" class="mr-2" style="min-width: 80px; justify-content: center;">
                Own
              </v-chip>
              <span class="text-body-2 font-weight-bold">{{ ownVinCount }}</span>
              <span class="text-caption text-grey ml-1">VINs</span>
            </div>
            <div v-for="src in sources" :key="src.sourceJuid" class="d-flex align-center mb-1">
              <v-chip size="x-small" color="blue" variant="tonal" class="mr-2" style="min-width: 80px; justify-content: center;">
                {{ src.sourceJuid }}
              </v-chip>
              <span class="text-body-2 font-weight-bold">{{ src.contributedVins ?? '—' }}</span>
              <span class="text-caption text-grey ml-1">VINs</span>
            </div>
            <v-divider class="my-2" />
            <div class="text-subtitle-2 font-weight-bold">
              Total: {{ totalVehicles }} VINs
            </div>
          </v-card>

          <!-- Fallback: simple totals when sourceJazelId data is not yet available -->
          <v-card v-else class="flow-node flow-node--account mb-3" variant="outlined">
            <div class="d-flex align-center mb-1">
              <v-icon size="18" color="primary" class="mr-2">mdi-store</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ accountId }}</span>
              <v-chip size="x-small" color="primary" variant="tonal" class="ml-2">This Account</v-chip>
            </div>
            <div class="text-h6 font-weight-bold mt-1">{{ totalVehicles }} <span class="text-body-2 font-weight-regular text-grey">VINs total</span></div>
            <div class="text-caption text-grey">
              {{ stats?.sourceCounts?.feed ?? 0 }} feed · {{ stats?.sourceCounts?.manual ?? 0 }} manual
            </div>
            <v-alert type="info" variant="tonal" density="compact" class="mt-2 text-caption">
              Per-source breakdown will appear after the next feed import
            </v-alert>
          </v-card>

          <!-- Source accounts (config details) -->
          <v-card
            v-for="(src, idx) in sources"
            :key="idx"
            class="flow-node flow-node--source mb-3"
            variant="outlined"
          >
            <div class="d-flex align-center mb-1">
              <v-icon size="18" color="blue-darken-1" class="mr-2">mdi-account-circle</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ src.sourceJuid }}</span>
              <v-chip size="x-small" color="blue" variant="tonal" class="ml-2">Source</v-chip>
            </div>
            <div v-if="src.contributedVins !== null" class="text-h6 font-weight-bold mt-1">
              {{ src.contributedVins }} <span class="text-body-2 font-weight-regular text-grey">VINs contributed after filters</span>
            </div>
            <div v-else class="text-caption text-warning mt-1">
              Contribution count unavailable until the next feed import
            </div>
            <div v-if="src.sourceTotalVins !== null" class="text-caption text-grey mt-1">
              Source inventory: {{ src.sourceTotalVins }} VINs
            </div>
            <div v-if="src.sourceFeed" class="text-caption text-grey mt-1">
              Feed: {{ src.sourceFeed }}
            </div>
            <div v-if="src.filterSummary" class="text-caption text-grey">
              Filter: {{ src.filterSummary }}
            </div>
          </v-card>
        </template>

        <!-- SHARED INVENTORY: feed info + filters + per-source counts -->
        <template v-if="mode === 'sharedInventory'">
          <!-- Feed + Pattern info -->
          <v-card class="flow-node flow-node--feed mb-3" variant="outlined">
            <div class="d-flex align-center mb-1">
              <v-icon size="18" color="teal-darken-1" class="mr-2">mdi-rss</v-icon>
              <span class="text-subtitle-2 font-weight-bold">Feed Source</span>
            </div>
            <div class="text-body-2">{{ feedSource || 'N/A' }}</div>
            <div v-if="pattern" class="text-caption text-grey mt-1">
              Pattern: <code>{{ pattern }}</code>
            </div>
          </v-card>

          <!-- Filters summary -->
          <v-card v-if="sharedFilters.length > 0" class="flow-node flow-node--filters mb-3" variant="outlined">
            <div class="d-flex align-center mb-1">
              <v-icon size="18" color="orange-darken-1" class="mr-2">mdi-filter-outline</v-icon>
              <span class="text-subtitle-2 font-weight-bold">Filters ({{ sharedFilters.length }})</span>
            </div>
            <div v-for="(f, i) in sharedFilters" :key="i" class="text-caption text-grey">
              {{ i + 1 }}. {{ formatFilter(f) }}
            </div>
          </v-card>

          <!-- Per-feed-source VIN counts -->
          <v-card class="flow-node flow-node--counts" variant="flat" elevation="1">
            <v-icon size="22" color="green" class="mb-1">mdi-chart-pie</v-icon>
            <div class="text-subtitle-2 font-weight-bold mb-1">VIN Breakdown</div>
            <div v-if="feedSourceEntries.length === 0" class="text-caption text-grey">
              No feed source data yet
            </div>
            <div v-for="entry in feedSourceEntries" :key="entry.source" class="d-flex align-center mb-1">
              <v-chip
                size="x-small"
                :color="entry.color"
                variant="tonal"
                class="mr-2 text-uppercase"
                style="min-width: 80px; justify-content: center;"
              >{{ entry.source }}</v-chip>
              <span class="text-body-2 font-weight-bold">{{ entry.count }}</span>
              <span class="text-caption text-grey ml-1">VINs</span>
            </div>
            <v-divider class="my-2" />
            <div class="text-subtitle-2 font-weight-bold">
              Total: {{ totalVehicles }} VINs
            </div>
          </v-card>
        </template>
      </div>
    </div>
  </ConfigPageShell>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { getCurrentInventoryStats } from '@/api'
import {
  fetchGroupSiteConfig,
  type GroupSiteConfigResponse,
  type GroupSiteFilter,
} from '@/services/groupSiteConfigService'
import { useAccountSettingsStore } from '@/stores/accountSettings'

const accountSettings = useAccountSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const loading = ref(false)
const errorMsg = ref('')
const stats = ref<Record<string, any> | null>(null)
const config = ref<GroupSiteConfigResponse | null>(null)
const sourceStats = ref<Map<string, Record<string, any>>>(new Map())

const accountId = computed(() => accountSettings.accountId || '')
const mode = computed(() => config.value?.mode || 'none')
const totalVehicles = computed(() => stats.value?.totalVehicles ?? 0)
const feedSource = computed(() => config.value?.feedSource || '')
const pattern = computed(() => config.value?.pattern || '')
const sharedFilters = computed(() => config.value?.filters || [])

const sourceJazelIdCounts = computed<Record<string, number>>(() =>
  stats.value?.sourceJazelIdCounts && typeof stats.value.sourceJazelIdCounts === 'object'
    ? stats.value.sourceJazelIdCounts
    : {},
)

const sourceBreakdownAvailable = computed(() => Object.keys(sourceJazelIdCounts.value).length > 0)

const ownVinCount = computed(() => {
  if (!sourceBreakdownAvailable.value) return null
  const fromSources = Object.values(sourceJazelIdCounts.value).reduce((a, b) => a + b, 0)
  return Math.max(0, totalVehicles.value - fromSources)
})

const sources = computed(() =>
  (config.value?.sources || []).map(s => {
    const countFromBreakdown = sourceJazelIdCounts.value[s.sourceJuid]
    const srcTotalVins = sourceStats.value.get(s.sourceJuid)?.totalVehicles
    return {
      sourceJuid: s.sourceJuid,
      sourceFeed: s.sourceFeed || '',
      filterSummary: formatFilter(s.filter),
      // Prefer the breakdown count (VINs from this source in THIS account)
      contributedVins: typeof countFromBreakdown === 'number' ? countFromBreakdown : null,
      // Fallback: total VINs in the source account itself
      sourceTotalVins: typeof srcTotalVins === 'number' ? srcTotalVins : null,
    }
  }),
)

const COLORS = ['blue', 'green', 'orange', 'red', 'purple', 'cyan', 'brown', 'blue-grey']

const feedSourceEntries = computed(() => {
  const fsc = stats.value?.feedSourceCounts
  if (!fsc || typeof fsc !== 'object') return []
  return Object.entries(fsc as Record<string, number>)
    .sort(([, a], [, b]) => b - a)
    .map(([source, count], i) => ({
      source,
      count,
      color: COLORS[i % COLORS.length],
    }))
})

function formatFilter(f: GroupSiteFilter): string {
  if (!f) return ''
  const parts: string[] = []
  if (Array.isArray(f.condition) && f.condition.length > 0) {
    parts.push(f.condition.join(', '))
  } else if (typeof f.condition === 'string' && f.condition) {
    parts.push(f.condition)
  }
  if (f.make) parts.push(f.make)
  if (f.model) parts.push(f.model)
  if (f.trim) parts.push(`trim: ${f.trim}`)
  if (f.yearMin || f.yearMax) parts.push(`${f.yearMin || '?'}-${f.yearMax || '?'}`)
  if (f.bodyType) parts.push(f.bodyType)
  if (f.drivetrain) parts.push(f.drivetrain)
  if (f.fuelType) parts.push(f.fuelType)
  if (f.exteriorColor) parts.push(f.exteriorColor)
  if (f.interiorColor) parts.push(`interior: ${f.interiorColor}`)
  if (f.priceMin || f.priceMax) parts.push(`$${f.priceMin || '?'}-$${f.priceMax || '?'}`)
  if (f.mileageMin || f.mileageMax) parts.push(`${f.mileageMin || '?'}-${f.mileageMax || '?'} mi`)
  if (f.keyword) parts.push(`keyword: ${f.keyword}`)
  return parts.length > 0 ? parts.join(' + ') : 'No filter'
}

async function loadAll() {
  loading.value = true
  errorMsg.value = ''
  sourceStats.value = new Map()
  const id = accountSettings.accountId
  if (!id) {
    errorMsg.value = 'No account selected'
    loading.value = false
    return
  }
  try {
    const [statsRes, configRes] = await Promise.all([
      getCurrentInventoryStats(endpoint, id),
      // Current inventory reflects the published processor configuration.
      fetchGroupSiteConfig(endpoint, id).catch(() => null),
    ])
    stats.value = statsRes
    config.value = configRes?.config ?? null

    // For group site: fetch stats for each source JUID in parallel
    const cfg = configRes?.config
    if (cfg?.mode === 'groupSite' && Array.isArray(cfg.sources) && cfg.sources.length > 0) {
      const juids = [...new Set(cfg.sources.map(s => s.sourceJuid).filter(Boolean))]
      const results = await Promise.all(
        juids.map(juid =>
          getCurrentInventoryStats(endpoint, juid).catch(() => null),
        ),
      )
      const map = new Map<string, Record<string, any>>()
      juids.forEach((juid, i) => {
        if (results[i]) map.set(juid, results[i])
      })
      sourceStats.value = map
    }
  } catch (e: any) {
    errorMsg.value = e?.message || 'Failed to load data'
  } finally {
    loading.value = false
  }
}

onMounted(loadAll)
</script>

<style scoped>
.flow-container {
  display: flex;
  align-items: flex-start;
  gap: 0;
  overflow-x: auto;
  padding: 16px 0;
}

.flow-primary {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.flow-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.flow-col--details {
  align-items: stretch;
  min-width: 240px;
  max-width: 360px;
}

.flow-node {
  padding: 16px 20px;
  text-align: center;
  border-radius: 12px;
}

.flow-node--root {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  min-width: 140px;
}

.flow-node--mode {
  min-width: 160px;
}

.flow-node--account,
.flow-node--source,
.flow-node--feed,
.flow-node--filters {
  text-align: left;
  padding: 12px 16px;
}

.flow-node--counts {
  text-align: center;
  background: #f1f8e9;
}

.flow-connector {
  display: flex;
  align-items: center;
  padding: 0 8px;
  flex-shrink: 0;
}

.flow-line {
  width: 32px;
  height: 2px;
  background: #bdbdbd;
}
</style>